/**
 * Home のウィジェット本体。
 *
 * サイズは 1x1〜4x4（行×列）。幅 1 マスは数値ひとつだけ、高さ 1 マスはヘッダーと本文の 2 段、
 * それ以外はカードにし、幅と高さに応じて補足や一覧を足す。
 * データが無いときもレイアウトを動かさないよう、null を返さずに空表示を出す。
 * どれをホームに置くかは home-widget-registry が決める。
 */

import { differenceInCalendarDays, format } from 'date-fns'
import {
  Bell,
  CircleAlert,
  Dumbbell,
  Footprints,
  ListTodo,
  Loader2,
  Music4,
  Plus,
  Scale,
  UtensilsCrossed,
} from 'lucide-react'
import { useState } from 'react'

import type { EufySummaryResponse } from '@/api/types/eufy'
import type { GarminDashboardResponse } from '@/api/types/garmin'
import type { NotificationLogRead } from '@/api/types/notification'
import type { DailyNutrition } from '@/api/types/nutrition'
import type { TodoResponse } from '@/api/types/todo'
import type { WorkoutSessionListItem } from '@/api/types/workout'
import {
  cardListRows,
  formatDeadline,
  isRow,
  isTile,
  isWide,
  type NavigateFn,
  ratioOf,
  WORKOUT_WINDOW_DAYS,
} from '@/components/home/home-widget-data'
import { TodoQuickAddDialog } from '@/components/home/todo-quick-add-dialog'
import { WidgetBar, WidgetCard, WidgetEmpty, WidgetRow, WidgetTile } from '@/components/home/widget-card'
import { AuthImage } from '@/components/ui/auth-image'
import { parseUtcDate } from '@/lib/date-utils'
import { cn } from '@/lib/utils'
import { PATHS } from '@/routes/paths'
import type { WidgetSize } from '@/store/useHomeLayoutStore'
import { useMusicPlayerStore } from '@/store/useMusicPlayerStore'

const AddTodoButton = ({ onClick, className }: { onClick: () => void; className?: string }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label="TODO を追加"
    className={cn(
      'flex size-6 items-center justify-center rounded-full border-none bg-sky-500/15 text-sky-400 active:opacity-70',
      className,
    )}
  >
    <Plus size={14} />
  </button>
)

/**
 * ＋でクイック追加ダイアログを開く。
 * カード自体が button なので、＋は入れ子にせず兄弟として右上に重ねる。
 * 幅 1 マスでは＋を置く余地が無いので、＋ボタンだけを出す。
 */
export const TodoWidget = ({
  todos,
  size,
  onNavigate,
  onAdded,
}: {
  todos: TodoResponse[]
  size: WidgetSize
  onNavigate: NavigateFn
  onAdded: () => void
}) => {
  const [isAddOpen, setIsAddOpen] = useState(false)
  const openAdd = () => setIsAddOpen(true)

  return (
    <>
      {isTile(size) ? (
        <button
          type="button"
          onClick={openAdd}
          aria-label="TODO を追加"
          className="flex h-full w-full flex-col items-center justify-between overflow-hidden rounded-2xl border border-border/60 bg-card/60 p-2 active:opacity-70"
        >
          <ListTodo size={20} className="shrink-0 text-sky-400" />
          <Plus size={24} className="shrink-0" />
          <span className="max-w-full truncate text-[10px] leading-none text-muted-foreground">TODO</span>
        </button>
      ) : (
        <div className="relative h-full w-full">
          <TodoWidgetBody todos={todos} size={size} onNavigate={onNavigate} />
          <AddTodoButton onClick={openAdd} className={cn('absolute right-2', isRow(size) ? 'top-1' : 'top-2')} />
        </div>
      )}
      <TodoQuickAddDialog open={isAddOpen} onOpenChange={setIsAddOpen} onAdded={onAdded} />
    </>
  )
}

const TodoWidgetBody = ({
  todos,
  size,
  onNavigate,
}: {
  todos: TodoResponse[]
  size: WidgetSize
  onNavigate: NavigateFn
}) => {
  const now = new Date()
  const overdue = todos.filter((todo) => todo.deadline && parseUtcDate(todo.deadline) < now).length
  const rows = cardListRows(size)

  if (isRow(size)) {
    const next = todos[0]
    return (
      <WidgetRow
        icon={ListTodo}
        label="TODO"
        value={`${todos.length}`}
        unit="件"
        accentClassName={overdue > 0 ? 'text-red-400' : 'text-sky-400'}
        onClick={() => onNavigate(PATHS.TODO.HOME)}
      >
        <div className="flex items-center gap-1.5">
          {next && <span className="truncate text-xs">{next.title}</span>}
          {overdue > 0 && (
            <span className="ml-auto shrink-0 rounded-full bg-red-500/15 px-2 py-0.5 text-[10px] text-red-400">
              期限切れ {overdue}
            </span>
          )}
        </div>
      </WidgetRow>
    )
  }

  return (
    <WidgetCard icon={ListTodo} label="TODO" onClick={() => onNavigate(PATHS.TODO.HOME)} accentClassName="text-sky-400">
      <div className="flex items-baseline gap-1">
        <span className="text-2xl leading-none font-semibold">{todos.length}</span>
        <span className="text-[11px] text-muted-foreground">件</span>
        {overdue > 0 && (
          <span className="ml-auto rounded-full bg-red-500/15 px-2 py-0.5 text-[10px] text-red-400">
            期限切れ {overdue}
          </span>
        )}
      </div>
      {rows > 0 && (
        <ul className="mt-1.5 flex flex-col gap-0.5">
          {todos.slice(0, rows).map((todo) => (
            <li key={todo.id} className="flex items-center gap-1.5 text-xs">
              <span className="size-1 shrink-0 rounded-full bg-muted-foreground/60" />
              <span className="truncate">{todo.title}</span>
              {todo.deadline && (
                <span className="ml-auto shrink-0 text-[10px] text-muted-foreground">
                  {formatDeadline(todo.deadline)}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </WidgetCard>
  )
}

export const StepsWidget = ({
  garmin,
  onNavigate,
}: {
  garmin: GarminDashboardResponse | null
  onNavigate: NavigateFn
}) => (
  <WidgetTile
    icon={Footprints}
    label="歩数"
    value={garmin === null ? '-' : garmin.steps.toLocaleString()}
    accentClassName="text-emerald-400"
    onClick={() => onNavigate(PATHS.ACTIVITY)}
  />
)

/** 上からカロリー、P / F / C の達成度バー。幅のあるサイズでは実測値と目標値も出す */
export const NutritionWidget = ({
  nutrition,
  size,
  onNavigate,
}: {
  nutrition: DailyNutrition | null
  size: WidgetSize
  onNavigate: NavigateFn
}) => {
  if (isTile(size)) {
    return (
      <WidgetTile
        icon={UtensilsCrossed}
        label="kcal"
        value={nutrition === null ? '-' : Math.round(nutrition.total_calories).toLocaleString()}
        accentClassName="text-orange-400"
        onClick={() => onNavigate(PATHS.NUTRITION.HOME)}
      />
    )
  }

  const bars = nutrition
    ? [
        {
          key: 'P',
          actual: nutrition.total_protein_g,
          target: nutrition.target_protein_g,
          className: 'bg-red-400',
        },
        { key: 'F', actual: nutrition.total_fat_g, target: nutrition.target_fat_g, className: 'bg-yellow-400' },
        { key: 'C', actual: nutrition.total_carb_g, target: nutrition.target_carb_g, className: 'bg-sky-400' },
      ]
    : []
  const detailed = isWide(size)
  const target = nutrition?.target_calories == null ? null : Math.round(nutrition.target_calories).toLocaleString()
  const barRows = (
    <div className="flex flex-col gap-1.5">
      {bars.map(({ key, actual, target: barTarget, className }) => (
        <div key={key} className="flex items-center gap-1.5">
          <span className="w-2 text-[9px] leading-none text-muted-foreground">{key}</span>
          <WidgetBar ratio={ratioOf(actual, barTarget)} className={className} trackClassName="flex-1" />
          {detailed && (
            <span className="w-14 shrink-0 text-right text-[9px] leading-none text-muted-foreground tabular-nums">
              {Math.round(actual)}/{barTarget === null ? '-' : Math.round(barTarget)}g
            </span>
          )}
        </div>
      ))}
    </div>
  )

  if (isRow(size)) {
    return (
      <WidgetRow
        icon={UtensilsCrossed}
        label="食事"
        value={nutrition === null ? '-' : Math.round(nutrition.total_calories).toLocaleString()}
        sub={target === null ? 'kcal' : `/ ${target} kcal`}
        accentClassName="text-orange-400"
        onClick={() => onNavigate(PATHS.NUTRITION.HOME)}
      >
        {barRows}
      </WidgetRow>
    )
  }

  return (
    <WidgetCard
      icon={UtensilsCrossed}
      label="食事"
      onClick={() => onNavigate(PATHS.NUTRITION.HOME)}
      accentClassName="text-orange-400"
    >
      {nutrition === null ? (
        <WidgetEmpty message="記録なし" />
      ) : (
        <div className="flex h-full flex-col justify-center gap-1.5">
          <div className="flex items-baseline gap-1 tabular-nums">
            <span className="text-xl leading-none font-semibold">
              {Math.round(nutrition.total_calories).toLocaleString()}
            </span>
            <span className="text-[10px] text-muted-foreground">kcal</span>
            {target !== null && <span className="ml-auto text-[10px] text-muted-foreground">/ {target}</span>}
          </div>
          {barRows}
        </div>
      )}
    </WidgetCard>
  )
}

export const WeightWidget = ({
  weight,
  onNavigate,
}: {
  weight: EufySummaryResponse | null
  onNavigate: NavigateFn
}) => {
  const latest = weight?.latest ?? null
  const weightKg = latest?.weight_kg ?? null
  const elapsedDays =
    latest?.measured_at == null ? null : differenceInCalendarDays(new Date(), parseUtcDate(latest.measured_at))
  const measuredToday = elapsedDays !== null && elapsedDays <= 0

  return (
    <WidgetTile
      icon={Scale}
      label={elapsedDays === null || measuredToday ? '体重' : `${elapsedDays}日 未測定`}
      value={weightKg === null ? '-' : weightKg.toFixed(1)}
      accentClassName={elapsedDays === null ? undefined : measuredToday ? 'text-teal-400' : 'text-amber-400'}
      onClick={() => onNavigate(PATHS.NUTRITION.HOME)}
    />
  )
}

export const WorkoutWidget = ({
  sessions,
  size,
  onNavigate,
}: {
  sessions: WorkoutSessionListItem[]
  size: WidgetSize
  onNavigate: NavigateFn
}) => {
  const recentDayCount = new Set(
    sessions
      .map((session) => differenceInCalendarDays(new Date(), parseUtcDate(session.started_at)))
      .filter((days) => days >= 0 && days < WORKOUT_WINDOW_DAYS),
  ).size
  const active = sessions.find((session) => session.status === 'in_progress')
  const latest = sessions[0]
  const elapsedDays = latest ? differenceInCalendarDays(new Date(), parseUtcDate(latest.started_at)) : null
  const listRows = cardListRows(size)

  if (isTile(size)) {
    return (
      <WidgetTile
        icon={Dumbbell}
        label="直近1週"
        value={`${recentDayCount}`}
        accentClassName={active ? 'text-rose-400' : undefined}
        onClick={() => onNavigate(PATHS.WORKOUT.HOME)}
      />
    )
  }

  const subLabel = () => {
    if (active) return '記録中'
    if (elapsedDays === null) return '記録なし'
    if (elapsedDays === 0) return '今日やった'
    return `${elapsedDays}日前`
  }

  if (isRow(size)) {
    return (
      <WidgetRow
        icon={Dumbbell}
        label="筋トレ / 直近1週"
        value={`${recentDayCount}`}
        unit="日"
        accentClassName="text-rose-400"
        onClick={() => onNavigate(PATHS.WORKOUT.HOME)}
      >
        <span className={cn('block truncate text-[11px]', active ? 'text-rose-400' : 'text-muted-foreground')}>
          {subLabel()}
        </span>
      </WidgetRow>
    )
  }

  return (
    <WidgetCard
      icon={Dumbbell}
      label="筋トレ"
      onClick={() => onNavigate(PATHS.WORKOUT.HOME)}
      accentClassName="text-rose-400"
    >
      <div className="flex items-baseline gap-1">
        <span className="text-2xl leading-none font-semibold">{recentDayCount}</span>
        <span className="text-[11px] text-muted-foreground">日 / 直近1週間</span>
      </div>
      {listRows === 0 ? (
        <div className={cn('mt-2 truncate text-[10px]', active ? 'text-rose-400' : 'text-muted-foreground')}>
          {subLabel()}
        </div>
      ) : (
        <ul className="mt-1.5 flex flex-col gap-0.5">
          {sessions.slice(0, listRows).map((session) => (
            <li key={session.id} className="flex items-center gap-1.5 text-xs">
              <span className="shrink-0 text-[10px] text-muted-foreground tabular-nums">
                {format(parseUtcDate(session.started_at), 'M/d')}
              </span>
              <span className="truncate">{session.name ?? session.template_name ?? 'ワークアウト'}</span>
              <span className="ml-auto shrink-0 text-[10px] text-muted-foreground tabular-nums">
                {session.set_count}セット
              </span>
            </li>
          ))}
        </ul>
      )}
    </WidgetCard>
  )
}

export const MusicWidget = ({ size, onNavigate }: { size: WidgetSize; onNavigate: NavigateFn }) => {
  const track = useMusicPlayerStore((state) => state.queue[state.currentIndex])
  const history = useMusicPlayerStore((state) => state.playHistory[0])
  const current = track ?? history
  const productId = current && 'productId' in current ? current.productId : undefined
  const art = current?.thumbnailPath
    ? {
        src: current.thumbnailPath,
        alt: current.title,
        cacheKey: productId === undefined ? undefined : `home-music-${productId}`,
      }
    : null
  const stacked = !isWide(size)

  if (isTile(size)) {
    return (
      <button
        type="button"
        onClick={() => onNavigate(PATHS.MUSIC.LIBRARY)}
        aria-label={current ? current.title : 'ミュージック'}
        className="flex h-full w-full items-center justify-center overflow-hidden rounded-2xl border border-border/60 bg-card/60 p-0 active:opacity-70"
      >
        {art ? (
          <AuthImage {...art} className="size-full object-cover" />
        ) : (
          <Music4 size={20} className="text-[#fa243c]" />
        )}
      </button>
    )
  }

  if (isRow(size)) {
    return (
      <button
        type="button"
        onClick={() => onNavigate(PATHS.MUSIC.LIBRARY)}
        aria-label={current ? current.title : 'ミュージック'}
        className="flex h-full w-full items-center gap-2.5 overflow-hidden rounded-2xl border border-border/60 bg-card/60 p-2 text-left active:opacity-70"
      >
        <div className="flex aspect-square h-full shrink-0 items-center justify-center overflow-hidden rounded-md bg-muted">
          {art ? (
            <AuthImage {...art} className="size-full object-cover" />
          ) : (
            <Music4 size={18} className="text-[#fa243c]" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-xs font-medium">{current?.title ?? '再生履歴なし'}</div>
          {current?.artist && <div className="truncate text-[10px] text-muted-foreground">{current.artist}</div>}
        </div>
      </button>
    )
  }

  return (
    <WidgetCard
      icon={Music4}
      label={track ? '再生中' : '最近聴いた'}
      onClick={() => onNavigate(PATHS.MUSIC.LIBRARY)}
      accentClassName="text-[#fa243c]"
    >
      {!current ? (
        <WidgetEmpty message="再生履歴なし" />
      ) : (
        <div className={cn('flex h-full min-w-0 gap-2', stacked ? 'flex-col justify-center' : 'items-center')}>
          <div className={cn('shrink-0 overflow-hidden rounded-md bg-muted', stacked ? 'size-14' : 'size-12')}>
            {art && <AuthImage {...art} className="size-full object-cover" />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-xs font-medium">{current.title}</div>
            {current.artist && <div className="truncate text-[10px] text-muted-foreground">{current.artist}</div>}
          </div>
        </div>
      )}
    </WidgetCard>
  )
}

/** アイコン横のラベルは状態で変えない。「本日失敗」だと失敗が起きた合図に読めるため */
const NOTIFICATION_LABEL = 'ジョブ'

/** 実行中のジョブと当日のエラー件数 */
export const NotificationWidget = ({
  running,
  todayErrors,
  size,
  onNavigate,
}: {
  running: NotificationLogRead[]
  todayErrors: number
  size: WidgetSize
  onNavigate: NavigateFn
}) => {
  const listRows = cardListRows(size)
  const isRunning = running.length > 0
  const errorText = `失敗 ${todayErrors > 99 ? '99+' : todayErrors} 件`
  const accentClassName = isRunning ? 'text-sky-400' : todayErrors > 0 ? 'text-red-400' : undefined

  if (isTile(size)) {
    // 1x1 はラベルを出す余地がないので、状態はアイコンと色で伝える
    return (
      <WidgetTile
        icon={isRunning ? Loader2 : todayErrors > 0 ? CircleAlert : Bell}
        label={NOTIFICATION_LABEL}
        value={`${isRunning ? running.length : todayErrors}`}
        accentClassName={cn(isRunning && 'animate-spin', accentClassName)}
        onClick={() => onNavigate(PATHS.NOTIFICATIONS)}
      />
    )
  }

  if (isRow(size)) {
    return (
      <WidgetRow
        icon={Bell}
        label={NOTIFICATION_LABEL}
        value={`${running.length}`}
        unit="件"
        sub="実行中"
        accentClassName={accentClassName}
        onClick={() => onNavigate(PATHS.NOTIFICATIONS)}
      >
        <div className="flex min-w-0 flex-col gap-1">
          {running[0] ? (
            <>
              <span className="truncate text-[11px] leading-none">{running[0].title}</span>
              <span className="truncate text-[9px] leading-none text-muted-foreground">
                {running.length > 1 ? `${running[0].domain} 他 ${running.length - 1} 件` : running[0].domain}
              </span>
            </>
          ) : (
            <span className="truncate text-[10px] leading-none text-muted-foreground">実行中なし</span>
          )}
          {todayErrors > 0 && (
            <span className="truncate text-[9px] leading-none text-red-400 tabular-nums">本日の{errorText}</span>
          )}
        </div>
      </WidgetRow>
    )
  }

  return (
    <WidgetCard
      icon={Bell}
      label={NOTIFICATION_LABEL}
      onClick={() => onNavigate(PATHS.NOTIFICATIONS)}
      accentClassName={accentClassName}
    >
      <div className="flex h-full flex-col justify-center gap-1.5">
        <div className="flex items-center gap-1.5">
          {isRunning ? (
            <>
              <Loader2 size={16} className="shrink-0 animate-spin text-sky-400" />
              <span className="text-xl leading-none font-semibold">{running.length}</span>
              <span className="text-[11px] text-muted-foreground">件 実行中</span>
            </>
          ) : (
            <span className="text-[11px] text-muted-foreground">実行中のジョブはありません</span>
          )}
          {todayErrors > 0 && (
            <span className="ml-auto shrink-0 rounded-full bg-red-500/15 px-2 py-0.5 text-[10px] text-red-400 tabular-nums">
              本日の{errorText}
            </span>
          )}
        </div>
        {listRows > 0 && isRunning && (
          <ul className="flex flex-col gap-0.5">
            {running.slice(0, listRows).map((log) => (
              <li key={log.id} className="flex items-center gap-1.5 text-xs">
                <span className="shrink-0 text-[10px] text-muted-foreground">{log.domain}</span>
                <span className="truncate">{log.title}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </WidgetCard>
  )
}
